import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class FilterService {
    // Use a Map to store separate data lists for different components
    private dataListSubjects = new Map<string, BehaviorSubject<any[]>>();
    private filterTermsSubjects = new Map<string, BehaviorSubject<{ [key: string]: { term: string; type: string, timeline: string } }>>();
    private sortColumnSubjects = new Map<string, BehaviorSubject<string>>();
    private sortDirectionSubjects = new Map<string, BehaviorSubject<'asc' | 'desc'>>();
    private currentSortedColumnSubjects = new Map<string, BehaviorSubject<string | null>>();
    
    // Legacy support - default key for backward compatibility
    private readonly DEFAULT_KEY = 'default';
    
    // Legacy properties for backward compatibility
    private dataListSubject = this.getOrCreateDataListSubject(this.DEFAULT_KEY);
    private filterTermsSubject = this.getOrCreateFilterTermsSubject(this.DEFAULT_KEY);
    private sortColumnSubject = this.getOrCreateSortColumnSubject(this.DEFAULT_KEY);
    private sortDirectionSubject = this.getOrCreateSortDirectionSubject(this.DEFAULT_KEY);
    private currentSortedColumnSubject = this.getOrCreateCurrentSortedColumnSubject(this.DEFAULT_KEY);
    
    currentSortedColumn$ = this.currentSortedColumnSubject.asObservable();

    // Helper methods to get or create subjects
    private getOrCreateDataListSubject(key: string): BehaviorSubject<any[]> {
        if (!this.dataListSubjects.has(key)) {
            this.dataListSubjects.set(key, new BehaviorSubject<any[]>([]));
        }
        return this.dataListSubjects.get(key)!;
    }

    private getOrCreateFilterTermsSubject(key: string): BehaviorSubject<{ [key: string]: { term: string; type: string, timeline: string } }> {
        if (!this.filterTermsSubjects.has(key)) {
            this.filterTermsSubjects.set(key, new BehaviorSubject<{ [key: string]: { term: string; type: string, timeline: string } }>({}));
        }
        return this.filterTermsSubjects.get(key)!;
    }

    private getOrCreateSortColumnSubject(key: string): BehaviorSubject<string> {
        if (!this.sortColumnSubjects.has(key)) {
            this.sortColumnSubjects.set(key, new BehaviorSubject<string>(''));
        }
        return this.sortColumnSubjects.get(key)!;
    }

    private getOrCreateSortDirectionSubject(key: string): BehaviorSubject<'asc' | 'desc'> {
        if (!this.sortDirectionSubjects.has(key)) {
            this.sortDirectionSubjects.set(key, new BehaviorSubject<'asc' | 'desc'>('asc'));
        }
        return this.sortDirectionSubjects.get(key)!;
    }

    private getOrCreateCurrentSortedColumnSubject(key: string): BehaviorSubject<string | null> {
        if (!this.currentSortedColumnSubjects.has(key)) {
            this.currentSortedColumnSubjects.set(key, new BehaviorSubject<string | null>(null));
        }
        return this.currentSortedColumnSubjects.get(key)!;
    }

    // Public methods with component key support
    setCurrentSortedColumn(columnKey: string | null, componentKey: string = this.DEFAULT_KEY) {
        this.getOrCreateCurrentSortedColumnSubject(componentKey).next(columnKey);
    }

    setDataList(dataList: any, componentKey: string = this.DEFAULT_KEY): void {
        const normalizedList = Array.isArray(dataList) ? dataList : [];
        this.getOrCreateDataListSubject(componentKey).next(normalizedList);
    }

    setFilterTerm(column: any, term: string, componentKey: string = this.DEFAULT_KEY): void {
        const subject = this.getOrCreateFilterTermsSubject(componentKey);
        const currentTerms = subject.value;
        const updatedTerms = { ...currentTerms, [column.key]: { term, type: column.type, timeline: column.timeline } };
        subject.next(updatedTerms);
    }

    setSortColumn(column: string, componentKey: string = this.DEFAULT_KEY): void {
        this.getOrCreateSortColumnSubject(componentKey).next(column);
    }

    setSortDirection(direction: 'asc' | 'desc', componentKey: string = this.DEFAULT_KEY): void {
        this.getOrCreateSortDirectionSubject(componentKey).next(direction);
    }

    removeFilterTerm(columnKey: string, componentKey: string = this.DEFAULT_KEY): void {
        const subject = this.getOrCreateFilterTermsSubject(componentKey);
        const currentTerms = subject.value;
        const updatedTerms = { ...currentTerms };
        delete updatedTerms[columnKey]; 
        subject.next(updatedTerms);
    }

    getFilteredDataList$(componentKey: string = this.DEFAULT_KEY): Observable<any[]> {
        return combineLatest([
            this.getOrCreateDataListSubject(componentKey),
            this.getOrCreateFilterTermsSubject(componentKey),
            this.getOrCreateSortColumnSubject(componentKey),
            this.getOrCreateSortDirectionSubject(componentKey)
        ]).pipe(
            map(([dataList, filterTerms, sortColumn, sortDirection]) => {
                let filtered = dataList;

                Object.keys(filterTerms).forEach(columnKey => {
                    const { term, type, timeline } = filterTerms[columnKey];

                    if (!term) return;

                    filtered = filtered.filter(item => {
                        const itemValue = item[columnKey]?.toString();
                        const filterValue = new Date(term);
                        const itemDateValue = new Date(itemValue);

                        if (type === 'date') {
                            filterValue.setHours(0, 0, 0, 0);
                            itemDateValue.setHours(0, 0, 0, 0);
                        }

                        if (['date', 'time', 'dateTime'].includes(type)) {
                            if (timeline === 'start') {
                                return type === 'time' ? this.parseTime(itemValue) >= this.parseTime(term) : itemDateValue >= filterValue;
                            } else if (timeline === 'end') {
                                return type === 'time' ? this.parseTime(itemValue) <= this.parseTime(term) : itemDateValue <= filterValue;
                            } else {
                                return type === 'time' ? this.parseTime(itemValue) === this.parseTime(term) : itemDateValue.getTime() === filterValue.getTime();
                            }
                        } else {
                            return itemValue.toLowerCase().includes(term.toLowerCase());
                        }
                    });
                });

                if (sortColumn && sortDirection) {
                    filtered.sort((a, b) => {
                        const aValue = a[sortColumn]?.toString();
                        const bValue = b[sortColumn]?.toString();

                        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                        return 0;
                    });
                }

                return filtered;
            })
        );
    }

    // Legacy getter for backward compatibility
    get filteredDataList$(): Observable<any[]> {
        return this.getFilteredDataList$(this.DEFAULT_KEY);
    }

    private parseTime(timeString: string): number {
        const [hours, minutes] = timeString.split(':').map(Number);
        return hours * 60 + minutes;
    }
}
